import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Star, Trash2 } from "lucide-react";
import { fetchReviews, approveReview, featureReview, deleteReview } from "@admin/services/adminApi";
import { PageHeader, DataTable, Pagination, FilterBar, Pill } from "@admin/components/Primitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LIMIT = 20;

export default function AdminReviews() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [approved, setApproved] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchReviews({ approved: approved === "all" ? undefined : approved, skip, limit: LIMIT });
      setData(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load reviews"); }
    setLoading(false);
  }, [approved, skip]);

  useEffect(() => { load(); }, [load]);

  const onApprove = async (r, val) => {
    try { await approveReview(r.id, val); toast.success(val ? "Approved" : "Hidden"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onFeature = async (r, val) => {
    try { await featureReview(r.id, val); toast.success(val ? "Featured" : "Unfeatured"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onDelete = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await deleteReview(r.id); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    {
      key: "rating",
      label: "Rating",
      render: (r) => (
        <div className="flex gap-0.5 text-amber-500">
          {Array.from({ length: r.rating || 0 }).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
        </div>
      ),
    },
    {
      key: "name",
      label: "Reviewer",
      render: (r) => (
        <div>
          <p className="font-bold text-[#0A2540] text-sm">{r.name || r.user_name || "Anonymous"}</p>
          <p className="text-[10px] text-slate-400">{(r.created_at || "").slice(0, 10)}</p>
        </div>
      ),
    },
    { key: "lawyer_name", label: "Lawyer", render: (r) => <span className="text-sm">{r.lawyer_name || "—"}</span> },
    {
      key: "text",
      label: "Comment",
      render: (r) => <p className="text-sm text-slate-700 max-w-[400px] line-clamp-2">{r.text || r.comment || "—"}</p>,
    },
    {
      key: "approved",
      label: "State",
      render: (r) => (
        <div className="flex flex-col gap-1">
          {r.approved ? <Pill color="emerald">Approved</Pill> : <Pill color="amber">Pending</Pill>}
          {r.featured && <Pill color="gold">Featured</Pill>}
        </div>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="flex gap-1 flex-wrap min-w-[180px]">
          <button onClick={() => onApprove(r, !r.approved)} className="px-2 py-1 text-[11px] font-bold rounded-md border border-slate-200 hover:border-[#0A2540]" data-testid={`review-approve-${r.id}`}>
            {r.approved ? "Unapprove" : "Approve"}
          </button>
          <button onClick={() => onFeature(r, !r.featured)} className="px-2 py-1 text-[11px] font-bold rounded-md border border-slate-200 hover:border-[#D4AF37]" data-testid={`review-feature-${r.id}`}>
            {r.featured ? "Unfeature" : "Feature"}
          </button>
          <button onClick={() => onDelete(r)} className="px-2 py-1 text-[11px] font-bold rounded-md text-rose-700 border border-rose-200 hover:bg-rose-50" data-testid={`review-delete-${r.id}`}>
            <Trash2 size={10} className="inline" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader testid="admin-reviews-header" title="Reviews & Testimonials" subtitle="Approve, feature, or remove client reviews." />
      <FilterBar testid="reviews-filterbar">
        <Select value={approved} onValueChange={(v) => { setSkip(0); setApproved(v); }}>
          <SelectTrigger className="w-[200px]" data-testid="reviews-filter-approved"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All reviews</SelectItem>
            <SelectItem value="true">Approved only</SelectItem>
            <SelectItem value="false">Pending only</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <DataTable testid="reviews-table" columns={columns} rows={data.items} empty={loading ? "Loading…" : "No reviews"} />
      <Pagination testid="reviews-pagination" total={data.total} skip={skip} limit={LIMIT} onChange={setSkip} />
    </>
  );
}
